import { DEFAULT_MODEL_BY_PROVIDER, type ModelSlug, type ProviderKind } from "@t3tools/contracts";
import { normalizeModelSlug } from "@t3tools/shared/model";
import { memo, useEffect, useState } from "react";
import { type ProviderPickerKind, PROVIDER_OPTIONS } from "../../session-logic";
import { BoxIcon, ChevronDownIcon, Code2Icon, SparklesIcon } from "lucide-react";
import { type AppModelOption } from "../../appSettings";
import { getReasoningEffortLabel } from "../../modelPresentation";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "../ui/menu";
import { OpenAI, type Icon } from "../Icons";
import { cn } from "~/lib/utils";

function isAvailableProviderOption(option: (typeof PROVIDER_OPTIONS)[number]): option is {
  value: ProviderKind;
  label: string;
  available: true;
} {
  return option.available;
}

function resolveModelOptionForProviderPicker(
  provider: ProviderKind,
  value: string,
  options: ReadonlyArray<AppModelOption>,
): AppModelOption | null {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const direct = options.find((option) => option.slug === trimmedValue);
  if (direct) {
    return direct;
  }

  const byName = options.find((option) => option.name.toLowerCase() === trimmedValue.toLowerCase());
  if (byName) {
    return byName;
  }

  const normalized = normalizeModelSlug(trimmedValue, provider);
  if (!normalized) {
    return null;
  }

  const resolved = options.find((option) => option.slug === normalized);
  return resolved ?? null;
}

const PROVIDER_ICON_BY_PROVIDER: Record<ProviderPickerKind, Icon> = {
  codex: OpenAI,
};

export const AVAILABLE_PROVIDER_OPTIONS = PROVIDER_OPTIONS.filter(isAvailableProviderOption);

export const ProviderModelPicker = memo(function ProviderModelPicker(props: {
  provider: ProviderKind;
  model: ModelSlug;
  modelOptionsByProvider: Record<ProviderKind, ReadonlyArray<AppModelOption>>;
  compact?: boolean;
  disabled?: boolean;
  onProviderModelChange: (provider: ProviderKind, model: ModelSlug) => void;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectedProviderOptions = props.modelOptionsByProvider[props.provider];
  const selectedModelOption = resolveModelOptionForProviderPicker(
    props.provider,
    props.model,
    selectedProviderOptions,
  );
  const selectedModelLabel = selectedModelOption?.recommendedFor
    ? `${selectedModelOption.recommendedFor} · ${selectedModelOption.name.replace(/^GPT-\d+(?:\.\d+)? /, "")}`
    : (selectedModelOption?.name ?? props.model);
  const ProviderIcon = PROVIDER_ICON_BY_PROVIDER[props.provider];
  const recommendedOptions = selectedProviderOptions.filter((option) => option.recommendedFor);
  const otherOptions = selectedProviderOptions.filter((option) => !option.recommendedFor);

  const modelOptionContent = (option: AppModelOption) => {
    const RecommendationIcon =
      option.recommendedFor === "General"
        ? SparklesIcon
        : option.recommendedFor === "Coding"
          ? Code2Icon
          : BoxIcon;
    const detail = option.recommendedFor
      ? `${option.name}${
          option.defaultReasoningEffort
            ? ` · ${getReasoningEffortLabel(option.defaultReasoningEffort)} reasoning`
            : ""
        }`
      : (option.description ?? option.name);

    return (
      <span className="flex min-w-0 items-center gap-2.5 py-0.5">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <RecommendationIcon aria-hidden="true" className="size-3.5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex items-center gap-1.5 font-medium">
            {option.recommendedFor ?? option.name}
            {option.slug === DEFAULT_MODEL_BY_PROVIDER[props.provider] ? (
              <Badge size="sm" variant="secondary">
                Default
              </Badge>
            ) : null}
          </span>
          <span className="truncate text-muted-foreground text-xs">{detail}</span>
        </span>
      </span>
    );
  };

  useEffect(() => {
    if (props.disabled) {
      setIsMenuOpen(false);
    }
  }, [props.disabled]);

  return (
    <Menu
      open={isMenuOpen}
      onOpenChange={(open) => {
        if (props.disabled) {
          setIsMenuOpen(false);
          return;
        }
        setIsMenuOpen(open);
      }}
    >
      <MenuTrigger
        render={
          <Button
            size="sm"
            variant="ghost"
            className={cn(
              "min-w-0 shrink-0 whitespace-nowrap px-2 text-muted-foreground/70 hover:text-foreground/80",
              props.compact ? "max-w-42" : "sm:px-3",
            )}
            disabled={props.disabled}
          />
        }
      >
        <span
          className={cn("flex min-w-0 items-center gap-2", props.compact ? "max-w-36" : undefined)}
        >
          <ProviderIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground/70" />
          <span className="truncate">{selectedModelLabel}</span>
          <ChevronDownIcon aria-hidden="true" className="size-3 opacity-60" />
        </span>
      </MenuTrigger>
      <MenuPopup
        align="start"
        className="w-[min(23rem,calc(100vw-2rem))] [--available-height:min(28rem,70vh)]"
      >
        <MenuRadioGroup
          value={selectedModelOption?.slug ?? props.model}
          onValueChange={(value) => {
            if (props.disabled || !value) return;
            const resolvedOption = resolveModelOptionForProviderPicker(
              props.provider,
              value,
              selectedProviderOptions,
            );
            if (!resolvedOption) return;
            props.onProviderModelChange(props.provider, resolvedOption.slug);
            setIsMenuOpen(false);
          }}
        >
          {recommendedOptions.length > 0 ? (
            <MenuGroup>
              {recommendedOptions.map((option) => (
                <MenuRadioItem
                  key={`${props.provider}:${option.slug}`}
                  className="py-1.5"
                  value={option.slug}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {modelOptionContent(option)}
                </MenuRadioItem>
              ))}
            </MenuGroup>
          ) : null}
          {recommendedOptions.length > 0 && otherOptions.length > 0 ? <MenuSeparator /> : null}
          {otherOptions.length > 0 ? (
            <MenuGroup>
              <MenuGroupLabel>Other models</MenuGroupLabel>
              {otherOptions.map((option) => (
                <MenuRadioItem
                  key={`${props.provider}:${option.slug}`}
                  className="py-1.5"
                  value={option.slug}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {modelOptionContent(option)}
                </MenuRadioItem>
              ))}
            </MenuGroup>
          ) : null}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
});
